import {
 useState, useEffect, useCallback, useMemo, useRef,
} from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import { ROLE_PROFILE_TYPES } from 'views_shared/app/javascript/constants/profileTypes';

// Max wait for a deep-linked conversation to arrive via realtime refetch
// (e.g. V1 → V2 Kafka sync after a multi-send) before falling through to data[0].
const DEEP_LINK_WAIT_TIMEOUT_MS = 10000;

const PAGE_SIZE = 30;

// When a new-message receipt lands via realtime, interact_conversation_receivers_mat may not be
// refreshed yet. After the initial synced fetch, re-fetch on this backoff until the row reflects
// the message (its last_sent_at advances), then give up — the resync path backstops the rest.
const MAT_REFRESH_RETRY_DELAYS_MS = [750, 1500];

const sleep = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

function readDeepLinkFromUrl() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    v1Id: params.get('v1_conversation_id'),
    v2Uuid: params.get('conversation'),
  };
}

function hasDeepLinkParam({ v1Id, v2Uuid }) {
  return Boolean(v1Id || v2Uuid);
}

function findDeepLinkedConversation(conversations, { v1Id, v2Uuid }) {
  if (v2Uuid) return conversations.find((c) => c.conversation_id === v2Uuid) || null;
  if (v1Id) return conversations.find((c) => String(c.migrate_id) === v1Id) || null;
  return null;
}

// Mirror of the server-side filters in api/conversations.js — used to decide whether a
// previously-selected conv would still appear in a new tab/filter.

function convMatchesFilters(conv, args) {
  if (!conv) return false;
  const {
 mailbox, readStatus, role, requestId, showTrashed,
} = args || {};

  if (showTrashed && !conv.receipt_archived_at) return false;
  if (!showTrashed && conv.receipt_archived_at) return false;
  if (mailbox && conv.conversation_mailbox !== mailbox) return false;
  if (readStatus === 'unread' && conv.receipt_read_at) return false;
  if (readStatus === 'read' && !conv.receipt_read_at) return false;
  if (requestId && String(conv.origin_request_id) !== String(requestId)) return false;
  if (role) {
    const required = ROLE_PROFILE_TYPES[role];
    if (required) {
      const convTypes = conv.co_participant_profile_types || [];
      if (!required.some((t) => convTypes.includes(t))) return false;
    }
  }
  return true;
}

function mergeConversations(...arrays) {
  const map = new Map();
  arrays.forEach((arr) => {
    if (!arr) return;
    arr.forEach((conv) => {
      if (conv) map.set(conv.conversation_id, conv);
    });
  });
  const merged = Array.from(map.values());
  merged.sort((a, b) => {
    if (a.last_sent_at !== b.last_sent_at) return a.last_sent_at < b.last_sent_at ? 1 : -1;
    return a.conversation_id < b.conversation_id ? 1 : -1;
  });
  return merged;
}

function useConversations(
  currentReceiverProfileId,
  mailbox,
  filters,
  onMarkRead,
  isDesktop,
  showTrashed = false,
  suppressAutoSelect = false,
) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  // Set when a URL deep-link can't yet resolve in data (Kafka lag after V1 send).
  // Suppresses the data[0] fallback; cleared when the conversation arrives or timeout fires.
  const [deepLinkWaitStartTime, setDeepLinkWaitStartTime] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // True during a fresh fetch (mount or mailbox/filter change) — used to suppress the
  // empty-state flash before the first response arrives.
  const [isLoading, setIsLoading] = useState(true);
  const selectedRef = useRef(null);
  // Discard results from superseded fetches so an in-flight fetch from the previous
  // filter set can't clobber the new fetch's data by being the last to resolve.
  const fetchTokenRef = useRef(0);
  // Guards against IntersectionObserver firing loadMore repeatedly before state catches up.
  const isLoadingMoreRef = useRef(false);
  const conversationsRef = useRef(conversations);
  // Tracks the LAST FETCHED row, not the last DISPLAYED row — so an injected deep-linked
  // conv at the bottom of the displayed list doesn't make us skip in-between rows.
  const cursorRef = useRef(null);

  // Keep refs in sync so performFetch can read previous values without a stale closure
  selectedRef.current = selectedConversation;
  conversationsRef.current = conversations;

  // Read via ref so toggling compose mode doesn't recreate performFetch (which would force a
  // refetch). While a compose draft owns the right pane, never auto-select a real conversation —
  // that would push ?conversation= over the compose URL and steal the pane from the draft.
  const suppressAutoSelectRef = useRef(suppressAutoSelect);
  suppressAutoSelectRef.current = suppressAutoSelect;

  // Synchronously flip isLoading=true on fetch-input change so an empty previous tab
  // (e.g. Trash with 0 rows) doesn't carry its empty-state into the next tab during the
  // render between tab click and the useEffect → performFetch chain firing.
  const inputsKey = `${currentReceiverProfileId}|${mailbox}|${showTrashed}|${filters.status}|${filters.role}|${filters.posts}`;
  const prevInputsKeyRef = useRef(inputsKey);
  if (prevInputsKeyRef.current !== inputsKey) {
    prevInputsKeyRef.current = inputsKey;
    setIsLoading(true);
  }

  // Derive all conversation IDs from conversations
  const allIds = useMemo(() => conversations.map((c) => c.conversation_id), [conversations]);

  // Admin cross-user impersonation only
  const suppressAutoRead = globalThis.Qwoted?.IMPERSONATING_ADMIN === true;

  // Optimistically mark a conversation as read in the list (for bold/unbold styling).
  const markAsRead = useCallback(
    (conversation) => {
      if (!conversation || conversation.receipt_read_at || suppressAutoRead) return;

      const readAt = new Date().toISOString();
      setConversations((prev) => prev.map(
        (c) => (c.conversation_id === conversation.conversation_id ? { ...c, receipt_read_at: readAt } : c),
      ));

      qapi.conversations.markReadById(conversation.conversation_id).catch((err) => {
        qlog.error('Failed to mark conversation as read:', err);
      });

      onMarkRead?.(conversation);
    },
    [onMarkRead, suppressAutoRead],
  );

  // Always inject into the list at sorted position so the list highlight works even when
  // the conv lives outside the currently loaded pages — no-op if already there.
  const selectConversation = useCallback(
    (conversation) => {
      if (!conversation) {
        selectedRef.current = null;
        setSelectedConversation(null);
        return;
      }

      selectedRef.current = conversation;
      setSelectedConversation(conversation);
      setConversations((prev) => mergeConversations(prev, [conversation]));
      markAsRead(conversation);
    },
    [markAsRead],
  );

  // mode: "fresh" replaces the list (mount, filter change), "merge" keeps loaded pages 2+
  // and merges new page-1 rows in (refetch, realtime), "append" appends the next page.
  const performFetch = useCallback(
    async ({ mode }) => {
      const isAppend = mode === 'append';
      const isMerge = mode === 'merge';
      const isFresh = mode === 'fresh';

      if (isAppend && isLoadingMoreRef.current) return;
      if (isAppend && !cursorRef.current) return;

      fetchTokenRef.current += 1;
      const token = fetchTokenRef.current;

      const cursor = isAppend ? cursorRef.current : null;
      if (isAppend) {
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
      }
      if (isFresh) setIsLoading(true);

      try {
        const {
 data, error, totalRows, remainingRows,
} = await qapi.conversations.list(currentReceiverProfileId, {
          mailbox: showTrashed ? null : mailbox,
          readStatus: filters.status,
          role: filters.role,
          requestId: filters.posts,
          showTrashed,
          cursor,
          limit: PAGE_SIZE,
        });

        if (token !== fetchTokenRef.current) return; // stale fetch — superseded by a newer one

        if (error) {
          qlog.error('Failed to fetch conversations:', error);
          return;
        }

        const rows = data || [];

        if (isAppend) {
          setConversations((prev) => mergeConversations(prev, rows));
          if (rows.length > 0) {
            cursorRef.current = {
              lastSentAt: rows[rows.length - 1].last_sent_at,
              conversationId: rows[rows.length - 1].conversation_id,
            };
          }
          // PostgREST count=estimated can be wildly off; a short page is the only reliable
          // "no more available" signal.
          setHasMore(rows.length === PAGE_SIZE);
          return;
        }

        // Fresh / merge: handle empty result first.
        if (rows.length === 0) {
          setConversations([]);
          setSelectedConversation(null);
          cursorRef.current = null;
          setHasMore(false);
          return;
        }

        const prev = selectedRef.current;
        const prevInRows = prev && rows.some((c) => c.conversation_id === prev.conversation_id);
        // Filter args we just used for this fetch — used to decide whether prev still
        // belongs in the new view (preserve+inject) or is leaving it (clear selection).
        const filterArgs = {
          mailbox: showTrashed ? null : mailbox,
          readStatus: filters.status,
          role: filters.role,
          requestId: filters.posts,
          showTrashed,
        };
        const prevMatchesFilter = prev && convMatchesFilters(prev, filterArgs);

        let nextConversations;
        if (isMerge) {
          // Keep existing pages, replace stale page-1 rows with fresh ones. Server wins on dedup.
          nextConversations = mergeConversations(conversationsRef.current, rows);
        } else if (prev && !prevInRows && prevMatchesFilter) {
          // Fresh + selection still belongs in this view (covers both the deep-link race
          // and tab/filter switches where prev is on a later page). Inject at sorted position.
          nextConversations = mergeConversations(rows, [prev]);
        } else {
          nextConversations = rows;
        }

        setConversations(nextConversations);

        // Merge: bottom of merged list (preserves user's pagination depth). Fresh: bottom of
        // fetched rows — NOT the injected conv, which would skip in-between rows on next page.
        const cursorSource = isMerge ? nextConversations : rows;
        if (cursorSource.length > 0) {
          const last = cursorSource[cursorSource.length - 1];
          cursorRef.current = { lastSentAt: last.last_sent_at, conversationId: last.conversation_id };
        }

        // hasMore: prefer totalRows (works when merged list spans multiple pages); fall back to
        // remainingRows from Content-Range, then to "did this fetch fill its page" as last resort.
        if (typeof totalRows === 'number') {
          setHasMore(nextConversations.length < totalRows);
        } else if (typeof remainingRows === 'number') {
          setHasMore(remainingRows > 0);
        } else {
          setHasMore(rows.length === PAGE_SIZE);
        }

        // Auto-select / wait state only fires on fresh mode; merge inherits any existing
        // wait state and shouldn't pick rows[0] arbitrarily on a refetch.
        if (!prev) {
          const deepLink = readDeepLinkFromUrl();
          const deepLinked = findDeepLinkedConversation(rows, deepLink);
          if (deepLinked) {
            setSelectedConversation(deepLinked);
            setDeepLinkWaitStartTime(null);
          } else if (hasDeepLinkParam(deepLink) && isFresh) {
            setDeepLinkWaitStartTime((prevStart) => prevStart || Date.now());
          } else if (isFresh && isDesktop && !suppressAutoSelectRef.current) {
            setSelectedConversation(rows[0]);
          }
        } else if (isFresh && !prevInRows && !prevMatchesFilter) {
          // Selection no longer belongs in this view (e.g. switched to Trash with a
          // non-trashed conv selected). Clear / fall back to rows[0] — don't strand the user.
          // Suppressed (merged Everything feed / compose): clear only — the caller owns
          // auto-selection there and a virtual pitch row may outrank rows[0].
          setSelectedConversation(isDesktop && !suppressAutoSelectRef.current ? rows[0] : null);
        } else if (isMerge && !nextConversations.some((c) => c.conversation_id === prev.conversation_id)) {
          // The selected conv was evicted (action removed it) and isn't on page 1 either.
          // Fall back to the new top item — same UX as today's "selection no longer exists" case.
          setSelectedConversation(isDesktop ? nextConversations[0] : null);
        }
      } catch (err) {
        qlog.error('Failed to fetch conversations:', err);
      } finally {
        if (isAppend) {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        }
        // Only the most recent fresh fetch may clear isLoading — a stale fetch finishing
        // late must not unblock the empty-state during the newer fetch's pending window.
        if (isFresh && token === fetchTokenRef.current) setIsLoading(false);
      }
    },
    [currentReceiverProfileId, mailbox, filters, isDesktop, showTrashed],
  );

  const fetchFresh = useCallback(() => performFetch({ mode: 'fresh' }), [performFetch]);
  const refetch = useCallback(() => performFetch({ mode: 'merge' }), [performFetch]);
  const loadMore = useCallback(() => performFetch({ mode: 'append' }), [performFetch]);

  // Sync remove + sync ref update so the immediately-following refetch's merge sees the
  // post-evict state (the merge can't otherwise tell "filter excludes" from "on later page").
  const evictConversations = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    const idSet = new Set(ids);
    const filtered = conversationsRef.current.filter((c) => !idSet.has(c.conversation_id));
    conversationsRef.current = filtered;
    setConversations(filtered);
  }, []);

  // Filter-aware optimistic patch: if the patched conv would no longer match the active
  // filter (e.g. mark-read on the Unread tab), evict instead — otherwise the row lingers.
  const patchConversations = useCallback(
    (patches) => {
      if (!patches || patches.length === 0) return;
      const filterArgs = {
        mailbox: showTrashed ? null : mailbox,
        readStatus: filters.status,
        role: filters.role,
        requestId: filters.posts,
        showTrashed,
      };
      const fieldsById = new Map();
      const idsToEvict = [];
      patches.forEach((p) => {
        const current = conversationsRef.current.find((c) => c.conversation_id === p.id);
        if (!current) return;
        const patched = { ...current, ...p.fields };
        if (convMatchesFilters(patched, filterArgs)) {
          fieldsById.set(p.id, p.fields);
        } else {
          idsToEvict.push(p.id);
        }
      });
      let next = conversationsRef.current;
      if (fieldsById.size > 0) {
        next = next.map((c) => {
          const fields = fieldsById.get(c.conversation_id);
          return fields ? { ...c, ...fields } : c;
        });
      }
      if (idsToEvict.length > 0) {
        const evictSet = new Set(idsToEvict);
        next = next.filter((c) => !evictSet.has(c.conversation_id));
      }
      conversationsRef.current = next;
      setConversations(next);
    },
    [mailbox, filters, showTrashed],
  );

  // Used by "Mark All as Read" — goes through patchConversations so the filter-aware
  // evict applies (e.g. on the Unread filter, all loaded convs vanish as expected).
  const markAllLoadedAsRead = useCallback(() => {
    const readAt = new Date().toISOString();
    const patches = conversationsRef.current
      .filter((c) => !c.receipt_read_at)
      .map((c) => ({ id: c.conversation_id, fields: { receipt_read_at: readAt } }));
    patchConversations(patches);
  }, [patchConversations]);

  // Auto-fetch on mount and when params change. Reset hasMore + cursor so a stale "yes more"
  // or stale cursor from the previous filter set doesn't bleed into the new fetch.
  useEffect(() => {
    setHasMore(false);
    cursorRef.current = null;
    fetchFresh();
  }, [fetchFresh]);

  // Give up and select data[0] after DEEP_LINK_WAIT_TIMEOUT_MS. Start time persists
  // across refetches so the deadline doesn't reset on each fetch.
  useEffect(() => {
    if (!deepLinkWaitStartTime) return undefined;
    const remaining = Math.max(0, DEEP_LINK_WAIT_TIMEOUT_MS - (Date.now() - deepLinkWaitStartTime));
    const timeoutId = setTimeout(() => {
      setDeepLinkWaitStartTime(null);
      if (!selectedRef.current && isDesktop && conversations.length > 0) {
        setSelectedConversation(conversations[0]);
      }
    }, remaining);
    return () => clearTimeout(timeoutId);
  }, [deepLinkWaitStartTime, conversations, isDesktop]);

  // Fetch one conversation with the current filters and merge it (server wins, re-sorts →
  // bumps to top). If it no longer matches the filter, evict it. Returns the fetched row (or
  // undefined if it was evicted, errored, or superseded by a newer fetch).
  const fetchOneConversation = useCallback(
    async (conversationId) => {
      // Snapshot the fetch token — if a filter change supersedes us mid-flight, discard the
      // result rather than merging a stale row into the new view.
      const token = fetchTokenRef.current;
      try {
        const { data, error } = await qapi.conversations.list(currentReceiverProfileId, {
          mailbox: showTrashed ? null : mailbox,
          readStatus: filters.status,
          role: filters.role,
          requestId: filters.posts,
          showTrashed,
          conversationId,
          limit: 1,
        });
        if (token !== fetchTokenRef.current) return undefined; // stale — superseded by a newer fetch
        if (error) {
          qlog.error('Failed to sync conversation:', error);
          return undefined;
        }
        const conv = data?.[0];
        if (conv) {
          const merged = mergeConversations(conversationsRef.current, [conv]);
          conversationsRef.current = merged;
          setConversations(merged);
        } else {
          evictConversations([conversationId]);
        }
        return conv;
      } catch (err) {
        qlog.error('Failed to sync conversation:', err);
        return undefined;
      }
    },
    [currentReceiverProfileId, mailbox, filters, showTrashed, evictConversations],
  );

  // Realtime sync: merge the affected conversation, avoiding a full page-1 refetch per event.
  // For a new-message receipt (eventType "INSERT"), the mat may lag the WAL event, so retry
  // until last_sent_at advances past its pre-event value (or the thread first appears). A
  // receipt's created_at is stamped just after its message's sent_at, so an absolute
  // last_sent_at >= receipt.created_at test would never pass — we watch for movement instead.
  // Read/archive updates don't move last_sent_at, so they take a single fetch and no retry.
  const syncConversation = useCallback(
    async (conversationId, eventType) => {
      if (!conversationId) return;

      const startToken = fetchTokenRef.current;
      const before = conversationsRef.current.find((c) => c.conversation_id === conversationId);
      const beforeLastSentAt = before?.last_sent_at ?? null;
      const caughtUp = (row) => Boolean(row) && (beforeLastSentAt === null || row.last_sent_at > beforeLastSentAt);

      const conv = await fetchOneConversation(conversationId);
      if (fetchTokenRef.current !== startToken) return; // a filter change superseded us
      if (eventType !== 'INSERT' || caughtUp(conv)) return;

      let settled = false;
      await MAT_REFRESH_RETRY_DELAYS_MS.reduce(async (prev, delay) => {
        await prev;
        if (settled) return;
        await sleep(delay);
        if (fetchTokenRef.current !== startToken) {
          settled = true;
          return;
        }
        const next = await fetchOneConversation(conversationId);
        if (fetchTokenRef.current !== startToken || caughtUp(next)) settled = true;
      }, Promise.resolve());
    },
    [fetchOneConversation],
  );

  // Mark currently-selected as read (for bold/unbold). No-op if already read locally.
  const markSelectedAsRead = useCallback(() => {
    if (suppressAutoRead) return;
    const conversation = selectedRef.current;
    if (!conversation) return;

    const conversationId = conversation.conversation_id;

    setConversations((prev) => {
      const match = prev.find((c) => c.conversation_id === conversationId);
      if (!match || match.receipt_read_at) return prev;

      const readAt = new Date().toISOString();

      qapi.conversations.markReadById(conversationId).catch((err) => {
        qlog.error('Failed to mark conversation as read:', err);
      });

      onMarkRead?.(conversation);

      return prev.map((c) => (c.conversation_id === conversationId ? { ...c, receipt_read_at: readAt } : c));
    });
  }, [onMarkRead, suppressAutoRead]);

  return {
    conversations,
    allIds,
    selectedConversation,
    /** Same object `selectConversation` updates synchronously; for popstate ordering with other listeners. */
    selectedConversationRef: selectedRef,
    selectConversation,
    markSelectedAsRead,
    refetch,
    loadMore,
    evictConversations,
    patchConversations,
    markAllLoadedAsRead,
    syncConversation,
    hasMore,
    isLoadingMore,
    isLoading,
    // True while suppressing data[0] auto-select to wait for a deep-linked conversation.
    isWaitingForDeepLink: deepLinkWaitStartTime !== null,
  };
}

export default useConversations;
